import { MenuRowShortcutKbd } from '@/components/ui/shortcut-kbd'

import { ContextMenuItem, ContextMenuSeparator } from '../editor-context-menu-shell'
import type { MenuActions } from './types/menu-actions'
import type { MenuRowDef } from './types/menu-row-def'

type MenuActionRowsProps<Id extends string> = {
  rows: ReadonlyArray<MenuRowDef<Id>>
  actions: MenuActions<Id>
  /** Runs the row's action (if any), then closes the menu. */
  onItemClick: (run?: () => void) => void
}

/**
 * Renders context-menu rows from a row list plus an actions object. A row is skipped when its
 * action says `hidden` (or, absent that, the row is `hiddenByDefault`), or when it has no `run`
 * and the row is `whenNoHandler: 'hidden'`. Separators always render.
 */
export function MenuActionRows<Id extends string>({
  rows,
  actions,
  onItemClick,
}: MenuActionRowsProps<Id>) {
  return (
    <>
      {rows.map((row, index) => {
        if (row === 'separator') return <ContextMenuSeparator key={`separator-${index}`} />

        const action = actions[row.id]
        const run = action?.run
        const whenNoHandler = row.whenNoHandler ?? 'enabled'

        if (action?.hidden ?? row.hiddenByDefault ?? false) return null
        if (!run && whenNoHandler === 'hidden') return null

        const disabledForNoHandler = !run && whenNoHandler === 'disabled'

        return (
          <ContextMenuItem
            key={row.id}
            onClick={disabledForNoHandler ? undefined : () => onItemClick(run)}
            disabled={disabledForNoHandler || action?.disabled}
          >
            {row.label}
            {row.shortcutKey ? <MenuRowShortcutKbd shortcutKey={row.shortcutKey} /> : null}
          </ContextMenuItem>
        )
      })}
    </>
  )
}
